import React, {
  FC, useContext, useEffect, useRef, useState,
} from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SubmissionNavigation from './SubmissionNavigation';
import Inspection from './inspection/Inspection';
import Sign from './sign/Sign';
import { Spinner } from '../components';
import api from '../api';
import { prepareFormData, prepareFormDataForExistingSubmission } from './utils/prepare';
import { SubmissionFormSteps } from './types';
import {
  Submission,
  InspectionValue,
  InspectorType,
  SubmissionType,
  SubmissionCategory,
  SubmissionCategoryItem,
} from '../types/submission';
import Template, { CategoryType, TemplateCategory, DvirTemplate } from '../types/template';
import useInspectionForm from './forms/useInspectionForm';
import useVehicleForm, { OdometerIsoUomLabels } from './forms/useVehicleForm';
import useOverallForm, { OverallFormFields } from './forms/useOverallForm';
import { DriverLaunchDataContext } from '../context/LaunchDataContext';
import useAttachments from './forms/useAttachments';
import { getMismatches } from './mismatch-modal/helpers';
import { MismatchType } from './mismatch-modal/constants';
import { API_VERSION, NEXT_WORK_ORDER_NUMBER } from '../api/paths';
import S3SignedLinkFileUploader from './utils/S3SignedLinkFileUploader';
import { Log } from '../utils/Log';
import MismatchModal from './mismatch-modal/MismatchModal';
import LeaveConfirmationModal from './leave-confirmation-modal/LeaveConfirmationModal';
import UomChangeConfirmationModal from './uom-change-confirmation-modal/UomChangeConfirmationModal';
import { EventsManagerContext } from '../context/EventsManagerContext';
import { useAlert } from '../hooks';
import { PageMode } from '../pages';
import { getCurrentState, sortCategoriesByPriority } from './utils/utils';
import { DvirStatus } from '../types/types';
import SubmitDvirRequest, { DvirDefect, DvirDefectItem } from '../api/model/auxRequest/SubmitDvirRequest';
import { filterSeasonalCategoryItems } from '../driver/utils';
import { DvirFailedSubmissionRetrierContext } from '../context/DvirFailedSubmissionRetrier';
import { NextWorkOrderNumberResponse } from '../api/types';
import { StoreContext } from '../context/StoreContext';
import { DvirHistoryData } from '../home/types';
import { DvirSubmitMode } from '../constants';

interface SubmissionFormProps {
  currentSubmission: DvirHistoryData;
  goToHomePage: () => void;
  hasNotifiedFormForClosingPage: boolean;
  onApproveCloseDvir: () => void;
  onAbortCloseDvir: () => void;
  mode: PageMode;
  currentDvirSubmitMode: DvirSubmitMode;
}

const SubmissionForm: FC<SubmissionFormProps> = ({
  currentSubmission,
  goToHomePage,
  hasNotifiedFormForClosingPage,
  onAbortCloseDvir,
  onApproveCloseDvir,
  mode,
  currentDvirSubmitMode,
}) => {
  const MILES_TO_KM = 1.60934;
  const { trailer, setPreTripCompleted, setPostTripCompleted } = useContext(StoreContext);

  const launchData = useContext(DriverLaunchDataContext);
  const {
    templateUrl, templateId, context, submissionUrl, allowTrailers: isIncludeTrailer,
  } = launchData;

  const { dvirScheduler } = useContext(DvirFailedSubmissionRetrierContext);

  const {
    location: { latitude, longitude },
    engineData,
    defaultOdometerIsoUom,
    driverData,
    address,
    vin,
    inspectionTripType,
    vehicleNum,
    auxFunctions,
    language,
  } = useContext(EventsManagerContext);

  const [dvirData, setDvirData] = useState<Submission>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMismatchApproved, setIsMismatchApproved] = useState<boolean>(false);
  const [mismatchNotificationType, setMismatchNotificationType] = useState<MismatchType | null>(null);
  const [step, setStep] = useState<SubmissionFormSteps>(SubmissionFormSteps.Inspect);
  const [template, setTemplate] = useState<DvirTemplate>(null);
  const [showLeaveConfirmationModal, setShowLeaveConfirmationModal] = useState<boolean>(false);
  const [showUomChangeConfirmationModal, setShowUomChangeConfirmationModal] = useState<boolean>(false);

  const navigationCallbacksRef = useRef<{ onApprove: VoidFunction; onAbort: VoidFunction }>({
    onApprove: () => {},
    onAbort: () => {},
  });

  const hasTrailer = useRef(isIncludeTrailer === 'true' && !!trailer?.vin);

  const alert = useAlert();
  const { t } = useTranslation();

  const isVehicleDvirSubmit = currentDvirSubmitMode === DvirSubmitMode.Vehicle
    || currentDvirSubmitMode === DvirSubmitMode.VehicleTrailer;
  const isTrailerDvirSubmit = currentDvirSubmitMode === DvirSubmitMode.Trailer
    || currentDvirSubmitMode === DvirSubmitMode.VehicleTrailer;

  const {
    form: attachmentsForm,
    addAttachment,
    editNote,
    deleteNote,
    deletePhoto,
    hasFormChanged: hasAttachmentsFormChanged,
  } = useAttachments(template, dvirData);
  const {
    form: inspectionForm,
    validate: validateInspectionForm,
    handleFormChange: handleInspectionFormChange,
    hasFormChanged: hasInspectionFormChanged,
    incompleteInspectionCategoryName,
  } = useInspectionForm(template, dvirData, isVehicleDvirSubmit, isTrailerDvirSubmit);
  const {
    form: vehicleForm,
    validate: validateVehicleForm,
    handleFormChange: handleVehicleFormChange,
    hasFormChanged: hasVehicleFormChanged,
  } = useVehicleForm({
    engineData,
    dvirData,
    inspectionTripType,
    defaultOdometerIsoUom,
  });
  const {
    form: overallForm,
    isFormFilled: isOverallFormFilled,
    handleFormChange: handleOverallFormChange,
  } = useOverallForm({ hasTrailer: hasTrailer.current, isVehicleDvirSubmit, isTrailerDvirSubmit });

  const isReadOnlyMode = mode === PageMode.View || mode === PageMode.HistoryView;

  const convertTemplateStructure = (templateData: DvirTemplate): Template => {
    const trailerCategories: TemplateCategory[] = [];

    if (templateData?.trailerTemplate) {
      templateData.trailerTemplate.categories.forEach((category) => {
        trailerCategories.push({ ...category, categoryType: CategoryType.Trailer });
      });
    }

    const categories: TemplateCategory[] = [
      ...templateData.categories.map((category) => ({ ...category, categoryType: CategoryType.Vehicle })),
      ...trailerCategories,
    ];

    return {
      ...templateData,
      categories,
    };
  };

  const getSubmissionData = async () => {
    try {
      setIsLoading(true);
      if (isReadOnlyMode) {
        setTemplate(null);

        const currentVin = currentSubmission ? currentSubmission.vin : vin;
        const submissionDate = currentSubmission?.submissionDate;

        const submissionData: Submission = await api.get(
          `${submissionUrl}/${API_VERSION}/companies/${context}/vehicles/${currentVin}/submissions/${submissionDate}`,
          { vin: currentVin, submissionDate, language },
        );

        setDvirData(sortCategoriesByPriority(submissionData));
      } else {
        auxFunctions.getAddress({});
        setDvirData(null);

        const trailerTemplateId = localStorage.getItem(`trailer_template_id_${driverData?.driverId}`);
        const templateResponse = await api.get<DvirTemplate>(`${templateUrl}/${API_VERSION}/${context}/${templateId}`, {
          id: templateId,
          trailerTemplateId: trailerTemplateId || '',
          isIncludeTrailer: hasTrailer.current ? 'true' : 'false',
          language,
        });

        const templateData = convertTemplateStructure(templateResponse);
        const seasonalTemplateData = filterSeasonalCategoryItems(templateData);
        setTemplate(seasonalTemplateData);
      }
    } catch (e) {
      alert.error({
        title: t('cloud_dvir__error_processing_dvir_record'),
        children: t('cloud_dvir__error_submit_dvir_descr'),
      });
    }
    setIsLoading(false);
  };

  if (dvirData && !overallForm[OverallFormFields.OverallVehicle].value) {
    handleOverallFormChange(OverallFormFields.OverallVehicle, getCurrentState(dvirData.events));
  }

  if (dvirData && !overallForm[OverallFormFields.OverallTrailer].value) {
    handleOverallFormChange(OverallFormFields.OverallTrailer, getCurrentState(dvirData.events));
  }

  const validateSubmissionForm = (): boolean => {
    const isValidVehicleForm = validateVehicleForm();
    const isValidInspectionForm = validateInspectionForm();

    return isValidVehicleForm && isValidInspectionForm;
  };

  const checkCriticalIssuesByType = (type: CategoryType): boolean => Object.entries(inspectionForm || {})
    .filter((a) => a[0].startsWith(`${type}-`))
    .map((a) => a[1])
    .flatMap((cat) => Object.values(cat.items))
    .some((i) => (i.isCritical && i.value === InspectionValue.NeedsRepair));

  const checkIssuesByType = (type: CategoryType): boolean => Object.entries(inspectionForm || {})
    .filter((a) => a[0].startsWith(`${type}-`))
    .map((a) => a[1])
    .flatMap((cat) => Object.values(cat.items))
    .some((i) => (i.value === InspectionValue.NeedsRepair));

  const checkIssues = () => {
    overallForm.hasCriticalIssue = {
      [OverallFormFields.OverallVehicle]: checkCriticalIssuesByType(CategoryType.Vehicle),
      [OverallFormFields.OverallTrailer]: checkCriticalIssuesByType(CategoryType.Trailer),
    };
    overallForm.hasIssue = {
      [OverallFormFields.OverallVehicle]: checkIssuesByType(CategoryType.Vehicle),
      [OverallFormFields.OverallTrailer]: checkIssuesByType(CategoryType.Trailer),
    };
  };

  const toMiles = (value: number, uom: string) => (uom === 'SMI' ? value : value / MILES_TO_KM);

  const isOdometerValueSuspicious = (): boolean => {
    if (engineData?.odometer == null || engineData?.odometer === '' || !engineData?.uom) {
      return false;
    }
    const engineOdometer = Number(engineData?.odometer);
    const formOdometer = Number(vehicleForm?.odometer?.value);
    const engineUom = (engineData?.uom ?? '').trim().toUpperCase();
    const formUom = (vehicleForm?.odometerIsoUom?.value ?? '').trim().toUpperCase();

    if (!Number.isFinite(engineOdometer) || engineOdometer === 0 || !Number.isFinite(formOdometer)) {
      return false;
    }
    if (!engineUom || !formUom) {
      return false;
    }
    if ((engineUom !== 'SMI' && engineUom !== 'KMT') || (formUom !== 'SMI' && formUom !== 'KMT')) {
      return false;
    }

    const engineInMiles = toMiles(engineOdometer, engineUom);
    const formInMiles = toMiles(formOdometer, formUom);

    if (engineInMiles < 100) {
      return false;
    }

    const toleranceRatio = 0.05;
    const tolerance = Math.max(Math.max(engineInMiles, formInMiles) * toleranceRatio, 10);
    const diffInMiles = Math.abs(engineInMiles - formInMiles);

    return diffInMiles > tolerance;
  };

  const handleStepChange = (nextStep: SubmissionFormSteps): void => {
    if (nextStep === SubmissionFormSteps.Sign) {
      const isValid = isReadOnlyMode || validateSubmissionForm();

      if (!isValid) {
        return;
      }

      if (isOdometerValueSuspicious()) {
        setShowUomChangeConfirmationModal(true);
        return;
      }
    }

    overallForm[OverallFormFields.OverallVehicle].value = null;

    if (hasTrailer) {
      overallForm[OverallFormFields.OverallTrailer].value = null;
    }

    checkIssues();
    setStep(nextStep);
  };

  const handleUomChangeConfirm = () => {
    setShowUomChangeConfirmationModal(false);
    overallForm[OverallFormFields.OverallVehicle].value = null;
    if (hasTrailer) {
      overallForm[OverallFormFields.OverallTrailer].value = null;
    }
    checkIssues();
    setStep(SubmissionFormSteps.Sign);
  };

  const handleUomChangeCancel = () => {
    setShowUomChangeConfirmationModal(false);
  };

  const handleApproveMismatch = () => {
    setIsMismatchApproved(true);
    setMismatchNotificationType(null);
  };

  const storeSignature = () => {
    const signId = driverData.driverId.replace(' ', '_');
    const signatureData = overallForm[OverallFormFields.Sign].value.src;
    if (!signatureData || !signatureData.startsWith('blob:')) {
      return;
    }
    fetch(signatureData)
      .then((response) => response.arrayBuffer())
      .then((data) => {
        const base64String = btoa(String.fromCharCode.apply(null, new Uint8Array(data)));
        const imageData = `data:image/jpeg;base64,${base64String}`;
        localStorage.setItem(`signature_${signId}`, imageData);
      });
  };

  const sendSubmission = async (submission) => {
    if (submission && !Object.keys(submission).length) {
      return;
    }

    setIsLoading(true);

    const submissionForm = { ...submission };

    try {
      if (mode !== PageMode.View && submissionForm.formData.status === DvirStatus.AwaitingMechanic) {
        await api.get<NextWorkOrderNumberResponse>(`${templateUrl}/${API_VERSION}/${NEXT_WORK_ORDER_NUMBER}/${context}`, {})
          .then((response) => {
            if (response.workOrderNumber) {
              submissionForm.formData = {
                ...submissionForm.formData,
                workOrderNumber: response.workOrderNumber,
              } as Submission;
            } else {
              Log.e('Work order number not in response:', response);
            }
          })
          .catch((e) => {
            alert.error({
              title: t('cloud_dvir__error_submit_dvir_title'),
              children: t('cloud_dvir__error_submit_dvir_descr'),
            });
            Log.e(`Failed to receive work order number for companyId|vin: ${submissionForm.formData.companyId}|${submissionForm.formData.vin}, submissionDate: ${submissionForm.formData.submissionDate}, exception: ${e.message}`);
          });
      }

      let response: Record<string, string>;
      const signId = overallForm[OverallFormFields.Sign].value.id;
      const signatureData = overallForm[OverallFormFields.Sign].value.src;
      const signatureBase64 = await fetch(signatureData)
        .then((signatureResponse) => signatureResponse.arrayBuffer())
        .then((data) => btoa(String.fromCharCode.apply(null, new Uint8Array(data))));

      type CategoryItemWithCritical = SubmissionCategoryItem & { critical?: boolean };

      const dvirDefects: DvirDefect[] = submissionForm.formData.categories
        .map((category: SubmissionCategory) => {
          const defectItems: DvirDefectItem[] = category.items
            .filter((item: CategoryItemWithCritical) => item.events.some(
              (event) => event.safetyStatus === InspectionValue.NeedsRepair,
            ))
            .map((item: CategoryItemWithCritical) => ({
              item: item.name,
              status: item.events[0].safetyStatus,
              note: item.events[0]?.note,
              critical: item.critical ?? false,
            }));
          return defectItems.length > 0 ? { category: category.name, items: defectItems } : null;
        })
        .filter(Boolean);
      let safeStatus;
      if (submissionForm.formData.status === DvirStatus.Completed && (submissionForm.formData.events && submissionForm.formData.events.length > 1)) {
        response = await api.put<Record<string, string>>(`${submissionUrl}/${API_VERSION}/events`, {}, {
          companyId: submissionForm.formData.companyId,
          vin: submissionForm.formData.vin,
          submissionDate: submissionForm.formData.submissionDate,
          event: {
            language,
            inspectorId: driverData.driverId,
            inspectorType: InspectorType.Driver,
            safetyStatus: overallForm[OverallFormFields.OverallVehicle].value,
            signature: { id: signId, src: signatureBase64 },
            updatedTime: new Date().toISOString(),
          },
          initialSafetyStatus: submissionForm.formData.initialSafetyStatus,
        });
      } else {
        submissionForm.formData.events[0].signature = { id: signId, src: signatureBase64 };
        safeStatus = submissionForm.formData.initialSafetyStatus;

        response = await (api.put<Record<string, string>>(`${submissionUrl}/${API_VERSION}`, {}, submissionForm.formData)
          .catch(async (e) => {
            try {
              await dvirScheduler?.saveSubmissionForm(submissionForm);
            } finally {
              const submitDvirRequest: SubmitDvirRequest = new SubmitDvirRequest(
                submissionForm.formData.inspectionTripType,
                safeStatus,
                submissionForm.formData.location,
                submissionForm.formData.odometer,
                submissionForm.formData.odometerIsoUom,
                submissionForm.formData.engineHours,
                JSON.stringify(dvirDefects),
                submissionForm.formData.submissionType,
                submissionForm.formData.vin,
                submissionForm.formData.driver,
              );
              auxFunctions.saveDvir(submitDvirRequest);
            }
            throw e;
          }));
      }

      submissionForm.attachments?.forEach((attachment) => {
        const signedUrl = response[attachment.id];
        if (signedUrl) {
          S3SignedLinkFileUploader.uploadImage(signedUrl, attachment.src)
            .then((data) => {
              Log.i('Successfully uploaded to S3:', data);
            })
            .catch((error) => {
              dvirScheduler?.saveSubmissionAttachment(attachment);
              Log.e('Failed upload to S3:', error);
            });
        }
      });

      if (driverData.driverId) {
        storeSignature();
      }

      alert.success({ title: t('cloud_dvir__success_submit_dvir_title'), hideAfterSeconds: 5000 });
      if (submissionForm.formData.submissionType === SubmissionType.Vehicle) {
        if (submissionForm.formData.inspectionTripType === 'PRE_TRIP') {
          setPreTripCompleted(true);
        } else if (submissionForm.formData.inspectionTripType === 'POST_TRIP') {
          setPostTripCompleted(true);
        }

        const submitDvirRequest: SubmitDvirRequest = new SubmitDvirRequest(
          mode === PageMode.View ? '' : submissionForm.formData.inspectionTripType,
          safeStatus,
          submissionForm.formData.location,
          submissionForm.formData.odometer,
          submissionForm.formData.odometerIsoUom,
          submissionForm.formData.engineHours,
          JSON.stringify(dvirDefects),
          submissionForm.formData.submissionType,
          submissionForm.formData.vin,
          submissionForm.formData.driver,
        );
        auxFunctions.submitDvir(submitDvirRequest);
      }

      goToHomePage();
    } catch (e) {
      alert.error({
        title: t('cloud_dvir__error_submit_dvir_title'),
        children: t('cloud_dvir__error_submit_dvir_descr'),
      });

      Log.e('Failed to submit DVIR form', e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const resolvedVehicleNum = vehicleNum || launchData.truckNumber;
    const missingIdentityFields = [
      !vin && 'vin',
      !resolvedVehicleNum && 'vehicleNum',
      !driverData?.driverId && 'driverId',
    ].filter(Boolean);

    if (missingIdentityFields.length > 0) {
      Log.e('DVIR submission blocked because startup data is incomplete:', missingIdentityFields);
      alert.error({
        title: t('cloud_dvir__error_submit_dvir_title'),
        children: t('cloud_dvir__error_submit_dvir_descr'),
      });
      return;
    }

    if (!isMismatchApproved) {
      const {
        [CategoryType.Vehicle]: vehicleMismatchType = MismatchType.NoMismatch,
        [CategoryType.Trailer]: trailerMismatchType = MismatchType.NoMismatch,
      } = getMismatches(inspectionForm, overallForm, isVehicleDvirSubmit, isTrailerDvirSubmit);

      if (mode === PageMode.View) {
        setMismatchNotificationType(null);
      } else if (vehicleMismatchType !== MismatchType.NoMismatch) {
        setMismatchNotificationType(vehicleMismatchType);
        return;
      } else if (trailerMismatchType !== MismatchType.NoMismatch) {
        setMismatchNotificationType(trailerMismatchType);
        return;
      }
    }

    if (!submissionUrl) {
      return;
    }

    if (mode === PageMode.View) {
      const submission = prepareFormDataForExistingSubmission(dvirData, overallForm, driverData, DvirStatus.Completed);

      sendSubmission(submission);

      return;
    }

    const { [CategoryType.Vehicle]: vehicleSubmission, [CategoryType.Trailer]: trailerSubmission } = prepareFormData({
      vehicleForm,
      inspectionForm,
      overallForm,
      attachmentsForm,
      launchData,
      latitude,
      longitude,
      address,
      timeToLive: template.timeToLive,
      driverData,
      vin,
      vehicleNum: resolvedVehicleNum,
      templateName: template.name,
      hasTrailer: hasTrailer.current,
      trailerData: trailer,
      isVehicleDvirSubmit,
      isTrailerDvirSubmit,
      language,
      vehicleTranslations: template.translations,
      trailerTranslations: template.trailerTemplate?.translations,
    });

    await sendSubmission(vehicleSubmission);

    if (hasTrailer.current && trailerSubmission) {
      await sendSubmission(trailerSubmission);
    }
  };

  const hasFormChanged = (): boolean => hasInspectionFormChanged() || hasAttachmentsFormChanged() || hasVehicleFormChanged();

  const handleApproveCloseDvir = () => {
    setShowLeaveConfirmationModal(false);
    onApproveCloseDvir();
  };

  const handleAbortCloseDvir = () => {
    setShowLeaveConfirmationModal(false);
    onAbortCloseDvir();
  };

  const handleApproveExit = () => {
    goToHomePage();
  };

  const handleAbortExit = () => {
    setShowLeaveConfirmationModal(false);
  };

  const handleExit = () => {
    if (!isReadOnlyMode && hasFormChanged()) {
      navigationCallbacksRef.current.onApprove = handleApproveExit;
      navigationCallbacksRef.current.onAbort = handleAbortExit;

      setShowLeaveConfirmationModal(true);

      return;
    }

    goToHomePage();
  };

  useEffect(() => {
    if (templateUrl && context) {
      getSubmissionData();
    }
  }, [mode, templateUrl, templateId, context]);

  useEffect(() => {
    if (hasNotifiedFormForClosingPage) {
      if (hasFormChanged()) {
        navigationCallbacksRef.current.onApprove = handleApproveCloseDvir;
        navigationCallbacksRef.current.onAbort = handleAbortCloseDvir;

        setShowLeaveConfirmationModal(true);

        return;
      }

      onApproveCloseDvir();
    }
  }, [hasNotifiedFormForClosingPage]);

  const content = {
    [SubmissionFormSteps.Inspect]: (
      <Inspection
        incompleteInspectionCategoryName={incompleteInspectionCategoryName}
        readOnly={isReadOnlyMode}
        isVehicleDvirSubmit={isVehicleDvirSubmit}
        isTrailerDvirSubmit={isTrailerDvirSubmit}
        vehicleForm={vehicleForm}
        onVehicleFormChange={handleVehicleFormChange}
        inspectionForm={inspectionForm}
        onInspectionFormChange={handleInspectionFormChange}
        attachmentsForm={attachmentsForm}
        onAddAttachment={addAttachment}
        onEditNote={editNote}
        onDeleteNote={deleteNote}
        onDeletePhoto={deletePhoto}
        isDriverSignOff={mode === PageMode.View}
        currentDvirSubmitMode={currentDvirSubmitMode}
      />
    ),
    [SubmissionFormSteps.Sign]: (
      <Sign
        overallForm={overallForm}
        isSignReadOnly={mode === PageMode.HistoryView}
        onFormChange={handleOverallFormChange}
        isOverallInspectionReadOnly={isReadOnlyMode}
        hasTrailer={hasTrailer.current}
        submissionType={dvirData?.submissionType}
        isVehicleDvirSubmit={isVehicleDvirSubmit}
        isTrailerDvirSubmit={isTrailerDvirSubmit}
      />
    ),
  };

  return (
    <>
      {isLoading && <Spinner />}
      <Box>
        <SubmissionNavigation
          isSubmitDisabled={!isOverallFormFilled || mode === PageMode.HistoryView}
          step={step}
          onStepChange={handleStepChange}
          onExit={handleExit}
          onSubmit={handleSubmit}
        />
        <Box
          sx={{
            paddingTop: 5,
            maxWidth: (theme) => theme.grid.tablet,
            margin: '0 auto',
            width: '100%',
          }}
        >
          {inspectionForm && content[step]}
        </Box>

        {!!mismatchNotificationType && (
          <MismatchModal onApprove={handleApproveMismatch} mismatchNotificationType={mismatchNotificationType} />
        )}

        {showLeaveConfirmationModal && (
          <LeaveConfirmationModal
            onApprove={navigationCallbacksRef.current.onApprove}
            onAbort={navigationCallbacksRef.current.onAbort}
          />
        )}

        {showUomChangeConfirmationModal && (
          <UomChangeConfirmationModal
            open={showUomChangeConfirmationModal}
            engineValue={engineData?.odometer ?? ''}
            engineUom={engineData?.uom && OdometerIsoUomLabels[engineData.uom] ? t(OdometerIsoUomLabels[engineData.uom]) : String(engineData?.uom ?? '')}
            fromValue={vehicleForm.odometer.value}
            fromUom={vehicleForm.odometerIsoUom.value && OdometerIsoUomLabels[vehicleForm.odometerIsoUom.value] ? t(OdometerIsoUomLabels[vehicleForm.odometerIsoUom.value]) : String(vehicleForm.odometerIsoUom.value ?? '')}
            onConfirm={handleUomChangeConfirm}
            onCancel={handleUomChangeCancel}
          />
        )}
      </Box>
    </>
  );
};

export default SubmissionForm;
